import { AlertCircle, ClipboardList } from "lucide-react";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { useState } from "react";
import { ContactFormSchema } from "./contactFormSchema";
import { Modal } from "../ui/Modal";
import { useAnalytics } from "../../hooks/useAnalytics";

// Must match the backend emr_consent_template row whose paragraphs are
// rendered in the dialog below; it is recorded against every signed consent.
export const EMR_CONSENT_TEMPLATE_VERSION = 3;

interface EmrAcceptanceProps {
  control: Control<ContactFormSchema>;
  errors: FieldErrors<ContactFormSchema>;
  handleInputFocus: (
    e: React.FocusEvent<
      HTMLInputElement | HTMLSelectElement | HTMLButtonElement
    >,
  ) => void;
  showPreamble?: boolean;
}

export function EmrAcceptance({
  control,
  errors,
  handleInputFocus,
  showPreamble = false,
}: EmrAcceptanceProps) {
  const [showDialog, setShowDialog] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);

  const { track } = useAnalytics();

  const handleAcceptConsent = (onChange: (value: boolean) => void) => {
    track("lp.contact_form.emr_consent.accepted");
    setConsentAccepted(true);
    onChange(true);
    setShowDialog(false);
  };

  const handleCheckboxChange = (
    checked: boolean,
    onChange: (value: boolean) => void,
  ) => {
    if (checked) {
      // If checking, show dialog first
      track("lp.contact_form.emr_consent.dialog_shown");
      setShowDialog(true);
    } else {
      // If unchecking, allow it directly
      track("lp.contact_form.emr_consent.unchecked");
      setConsentAccepted(false);
      onChange(false);
    }
  };

  const checkbox = (
    <div className="space-y-0">
      <div
        className={`relative flex items-start p-4 ${showPreamble ? "" : "rounded-lg"} bg-gray-50 hover:bg-gray-100 transition-colors duration-200 group`}
      >
        <div className="flex h-5 items-center">
          <Controller
            name="emrConsentAccepted"
            control={control}
            render={({ field }) => (
              <input
                id="emrConsentAccepted"
                data-test-id="input-emrConsentAccepted"
                type="checkbox"
                checked={consentAccepted}
                onChange={(e) =>
                  handleCheckboxChange(e.target.checked, field.onChange)
                }
                onFocus={handleInputFocus}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 transition-colors duration-200"
              />
            )}
          />
        </div>
        <div className="ml-3 text-sm leading-6">
          <label
            htmlFor="emrConsentAccepted"
            className="font-medium text-gray-700 group-hover:text-gray-900 transition-colors duration-200 cursor-pointer"
          >
            I authorize HealthMatch to collect my medical records for
            eligibility determination.
          </label>
        </div>
      </div>

      {errors.emrConsentAccepted && (
        <div className="text-red-500 text-sm flex items-center">
          <AlertCircle className="w-4 h-4 mr-1" />
          {errors.emrConsentAccepted.message}
        </div>
      )}

      {/* EMR Consent Dialog */}
      <Modal
        isOpen={showDialog}
        onClose={() => setShowDialog(false)}
        title="Authorization to release protected health information"
        contentClassName="max-w-4xl p-0"
      >
        <div className="max-h-[80vh] overflow-y-scroll prose prose-sm p-6">
          <p className="mb-4">
            I authorize HealthMatch, and any covered entities or vendors working
            on behalf of HealthMatch, to obtain copies of all my medical records
            and information pertaining to my medical care and history, and
            payment or billing for such medical care, for all dates of service
            from any and all of my (i) health care providers that have treated
            me; and (ii) health plans that have paid for health care services on
            my behalf. This includes medical records and information concerning
            HIV testing or treatment of AIDS, sexually transmitted and other
            communicable diseases, substance use disorders, mental
            health/psychiatric records, developmental disability records, and
            genetic information.
          </p>

          <p className="mb-4">
            The networks through which my records are retrieved require a
            contribution in return. I authorize HealthMatch and the vendors
            named above to upload to those networks the health information I
            have given HealthMatch, including my questionnaire answers and
            profile, which may include information of the sensitive types
            described above. Other network participants, including health care
            providers and health plans, may access it. My records cannot be
            retrieved under this Authorization without this contribution.
          </p>

          <p className="mb-4">
            I request that this information be released to HealthMatch, and my
            information contributed to those networks, to assist me in
            determining my eligibility for clinical trials and to provide my
            information to relevant clinical trial providers.
          </p>

          <p className="mb-4">
            HealthMatch is a healthcare technology company and not a healthcare
            provider. HealthMatch does not offer medical advice, diagnosis,
            treatment or any form of medical opinion or recommendation through
            its services or otherwise. By my acceptance of this Authorization, I
            hereby request, consent, and authorize HealthMatch to coordinate
            access to a healthcare provider who may be able to provide a
            clinical assessment related to its services. I understand that while
            HealthMatch facilitates such access to a healthcare provider on my
            behalf, I am free to choose any healthcare provider and to continue
            using HealthMatch services.
          </p>

          <p className="mb-6">
            I understand that I have the right to revoke this Authorization in
            writing, at any time, by sending a written request to HealthMatch,
            which shall be considered effective upon receipt. I understand that
            a revocation is not effective to the extent that any person or
            entity has already acted in reliance on my Authorization. I
            understand that I am not required to sign this Authorization and
            that my provider or health plan cannot condition treatment, payment,
            enrollment or eligibility for benefits on my execution of this
            Authorization. I have the right to receive a copy of this
            Authorization. I understand that once my information is disclosed
            pursuant to this Authorization, it may be re-disclosed by the
            recipient listed above and, in that case, may no longer be protected
            by HIPAA. This Authorization remains in effect for one (1) year,
            unless I revoke the Authorization at an earlier date.
          </p>

          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200">
            <Controller
              name="emrConsentAccepted"
              control={control}
              render={({ field }) => (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      track("lp.contact_form.emr_consent.declined");
                      setShowDialog(false);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Decline
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAcceptConsent(field.onChange)}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  >
                    I agree
                  </button>
                </>
              )}
            />
          </div>
        </div>
      </Modal>
    </div>
  );

  if (showPreamble) {
    return (
      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <div className="flex items-start gap-2 bg-gray-50 px-4 py-3 border-b border-gray-200">
          <ClipboardList className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-gray-600">
            To confirm your eligibility for this trial, we need your permission
            to access your medical records. We will only access the specific
            information relevant to the trial eligibility and will keep your
            data secure and confidential.
          </p>
        </div>
        {checkbox}
      </div>
    );
  }

  return checkbox;
}
